import React from "react";
import withStyles from "@material-ui/core/styles/withStyles";
import Button from "@material-ui/core/Button";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import harmReductionLandingStyle from "assets/jss/material-dashboard-pro-react/views/harmReductionLandingStyle.jsx";
import MainNavBar from "components/Navbars/MainNavBar.jsx";
import FooterNavBar from "components/Footer/FooterNavBar.jsx";
import { trackEvent } from "utils/analytics";

/* ===== kw2 campaign image assets ===== */
import heroVideoPlaceholder from "assets/img/kw2-images/CUYA_Web_LandingPage_Header_VideoPlaceholder.jpg";
import heroVideo from "assets/img/kw2-images/CUYA_RG_Website_v01.mp4";
import quoteBackground from "assets/img/kw2-images/CUYA_Web_LandingPage_QuoteBackground.jpg";
import photoRacingPulse from "assets/img/kw2-images/CUYA_Web_LandingPage_CalloutPhoot_RacingPulse.jpg";
import photoParanoia from "assets/img/kw2-images/CUYA_Web_LandingPage_CalloutPhoot_Paranoia.jpg";
import photoTroubleBreathing from "assets/img/kw2-images/CUYA_Web_LandingPage_CalloutPhoot_TroubleBreathing.jpg";
import photoHeavySweating from "assets/img/kw2-images/CUYA_Web_LandingPage_CalloutPhoot_HeavySweating.jpg";
import photoConfusion from "assets/img/kw2-images/CUYA_Web_LandingPage_CalloutPhoot_Confusion.jpg";
import photoDizziness from "assets/img/kw2-images/CUYA_Web_LandingPage_CalloutPhoot_Dizziness.jpg";
import photoChestPain from "assets/img/kw2-images/CUYA_Web_LandingPage_CalloutPhoot_ChestPain.jpg";
import photoSeizure from "assets/img/kw2-images/CUYA_Web_LandingPage_CalloutPhoot_Seizure.jpg";
import photoUnresponsiveness from "assets/img/kw2-images/CUYA_Web_LandingPage_CalloutPhoot_Unresponsiveness.jpg";
import iconTestStrip from "assets/img/kw2-images/CUYA_Web_LandingPage_Icon_TestStrip_200x200.png";
import iconNarcan from "assets/img/kw2-images/CUYA_Web_LandingPage_Icon_Narcan_200x200.png";
import iconPeople from "assets/img/kw2-images/CUYA_Web_LandingPage_Icon_People_200x200.png";

/* Before-you-use list items */
const beforeUseItems = [
  {
    icon: iconTestStrip,
    title: "Test your cocaine",
    text: "Get ",
    link: "free fentanyl test strips",
    linkAction: "testStrips",
    textAfter: " and make sure you know what you’re using.",
  },
  {
    icon: iconNarcan,
    title: "Keep Narcan® nearby",
    text: "Be ready to safely reverse an opioid overdose. ",
    link: "Find free Narcan®",
    linkAction: "narcan",
    textAfter: " near you.",
  },
  {
    icon: iconPeople,
    title: "Never use alone",
    text: "Call ",
    phone: "1-800-484-3731",
    textAfter: " to use with a volunteer who’ll call for help if you need it.",
  },
];

/* "Help yourself or someone else slow down" — accordion items */
const takeActionAccordion = [
  {
    title: "Calm your mind",
    points: [
      "Move to a quiet spot you usually find safe and comfortable.",
      "Ask someone you trust to stay by your side.",
      "Try calming breathing exercises to help you relax.",
    ],
  },
  {
    title: "Comfort your body",
    points: [
      "Sit quietly or lay down and just close your eyes for a while.",
      "Press ice packs or a wet towel against your neck, knees and armpits.",
      "Take a warm or cool shower.",
    ],
  },
  {
    title: "Fuel your recovery",
    points: [
      "Sip slowly on water or a caffeine-free sports drink.",
      "Eat something simple, like fruit or toast.",
      "Take a gentle supplement, like L-theanine or magnesium.",
    ],
  },
];

/* Real-stories testimonial cards */
const stories = [
  {
    quote:
      "You know, you can always use more later on, but you can never use less. Making sure you’re starting out with a lower dose instead of just starting right on with a giant line, which might be more likely to cause that panic factor.",
    name: "11:08 AM, Anonymous",
  },
  {
    quote:
      "Have some test strips on you to make sure that the substances you are taking are pure and aren’t mixed or cut with anything else.",
    name: "5:37 PM, Cuyahoga Co.",
  },
  {
    quote:
      "Make sure that you’re with somebody that can regulate you, make sure you don’t do too much, and stay with you throughout the night so nothing bad happens to you. Somebody that you can trust that’s not gonna leave you.",
    name: "11:24 PM, Cuyahoga Co.",
  },
  {
    quote:
      "Like with all things, just be smart and start slow and don’t overdo it. Have a buddy present.",
    name: "10:16 AM, Anonymous",
  },
  {
    quote:
      "First and foremost, don’t use alone. Make sure that the person who is with them knows how to respond if they were to experience an overdose. Make sure there’s Narcan on hand, and make sure the person can recognize what it might look like if they have taken too much.",
    name: "11:22 AM, Van Wert Co.",
  },
];

/* Symptom cards — photo backgrounds with text overlay (3×2 grid) */
const featuresRow1 = [
  {
    image: photoRacingPulse,
    title: "Racing pulse",
    text: "Your heart beats faster or pounds harder than it should.",
  },
  {
    image: photoParanoia,
    title: "Paranoia",
    text: "You feel panicky, distrustful or on edge without understanding why.",
  },
  {
    image: photoTroubleBreathing,
    title: "Trouble Breathing",
    text: "Your chest feels tight and it’s hard to take a deep breath.",
  },
];

const featuresRow2 = [
  {
    image: photoHeavySweating,
    title: "Heavy Sweating",
    text: "You’re unusually hot, like you can’t cool down.",
  },
  {
    image: photoConfusion,
    title: "Confusion",
    text: "You find it hard to focus or think clearly.",
  },
  {
    image: photoDizziness,
    title: "Dizziness",
    text: "You feel unsteady, like you might fall down or faint.",
  },
];

/* "Signs you need serious help" — photo-background emergency cards */
const seriousSigns = [
  {
    image: photoChestPain,
    title: "Chest Pain",
    text:
      "You feel squeezing, pressure or pain in the chest, neck, shoulders, jaw and/or back.",
  },
  {
    image: photoSeizure,
    title: "Seizure",
    text:
      "Their body jerks or twitches uncontrollably, which may cause them to fall, drool, and/or lose bladder control.",
  },
  {
    image: photoUnresponsiveness,
    title: "Unresponsiveness",
    text:
      "They lose consciousness and/or don’t respond to your voice or touch.",
  },
];

class HarmReductionLanding extends React.Component {
  state = {
    // index of the open accordion panel; first panel open by default
    openAccordion: 0,
  };

  // Refs + observer bookkeeping for the scroll-visibility tracking events.
  signsSectionRef = React.createRef();
  emergencySentinelRef = React.createRef();
  observers = [];
  dwellTimer = null;

  componentDidMount() {
    this.setupVisibilityTracking();
  }

  componentWillUnmount() {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.dwellTimer) clearTimeout(this.dwellTimer);
  }

  /* Wire the three scroll-visibility GA events with IntersectionObserver:
       - viewed_overdose_signs: signs section enters the viewport (once)
       - read_overdose_signs:   signs section stays in view 3+s continuous (once)
       - viewed_emergency_signs: bottom of the emergency card grid enters view (once)
     Guarded for environments without IntersectionObserver (older browsers /
     SSR); the click events still work regardless. */
  setupVisibilityTracking = () => {
    if (typeof window === "undefined" || typeof window.IntersectionObserver === "undefined") {
      return;
    }

    // #2 + #3: the signs section (viewed + 3s dwell).
    const signsEl = this.signsSectionRef.current;
    if (signsEl) {
      let viewedFired = false;
      let readFired = false;
      const signsObserver = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!viewedFired) {
                viewedFired = true;
                trackEvent("viewed_overdose_signs");
              }
              // start the 3s continuous-dwell timer
              if (!readFired && !this.dwellTimer) {
                this.dwellTimer = setTimeout(() => {
                  readFired = true;
                  this.dwellTimer = null;
                  trackEvent("read_overdose_signs");
                }, 3000);
              }
            } else if (this.dwellTimer) {
              // scrolled away before 3s — reset so dwell must be continuous
              clearTimeout(this.dwellTimer);
              this.dwellTimer = null;
            }
          });
        },
        { threshold: 0.3 }
      );
      signsObserver.observe(signsEl);
      this.observers.push(signsObserver);
    }

    // #4: bottom of the emergency card grid.
    const emergencyEl = this.emergencySentinelRef.current;
    if (emergencyEl) {
      let emergencyFired = false;
      const emergencyObserver = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !emergencyFired) {
              emergencyFired = true;
              trackEvent("viewed_emergency_signs");
              emergencyObserver.disconnect();
            }
          });
        },
        { threshold: 0 }
      );
      emergencyObserver.observe(emergencyEl);
      this.observers.push(emergencyObserver);
    }
  };

  toggleAccordion = (idx) => {
    this.setState((prev) => {
      const isOpening = prev.openAccordion !== idx;
      // Fire the safety-tip event only when a panel is being opened, not
      // when it's toggled closed (per the tracking spec).
      if (isOpening) trackEvent("viewed_safety_tip");
      return { openAccordion: isOpening ? idx : -1 };
    });
  };

  /* SPA navigation to a search-service page, optionally with a pre-applied
     filter. `filter` is an array of { name } objects matching the exact DB
     attribute names; ServiceSearch reads it from the ?filter= query param on
     mount (see ServiceSearch.componentDidMount). */
  goToService = (path, filter) => {
    const url = filter
      ? `${path}?filter=${JSON.stringify(filter)}`
      : path;
    this.props.history.push(url);
  };

  /* Hero "Learn the Signs" jump link: fire the KPI event, then smooth-scroll
     to the signs section (the primary goal of the page). */
  scrollToSigns = () => {
    trackEvent("clicked_learn_the_signs");
    const el = document.getElementById("signs-you-need-to-slow-down");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  /* Reusable filter constants — values verified against the live
     /api/v1/search/services API (see below), NOT just the locale files:
       - Harm-reduction service_types filter by NAME (cat_desc):
           "Narcan (Naloxone)" -> 56 results
           "Naloxbox"          -> 89 results  (NOT "Naloxbox Locations" => 0)
           "Testing Strips"    -> 30 results  (NOT "Fentanyl Testing Strips" => 0)
       - "Contingency Management Therapy" is a feature QUESTION (id 236) under
         "Interventions Used", so it filters by ID (filters), not cat_desc.
         Passing it as a name returns 0; filters:[236] -> 147 results.

     "Find free Narcan" intentionally filters to Narcan (Naloxone) ONLY —
     Naloxboxes were dropped per the client thread (Jul 8 2026, signsofOD.org):
     naloxboxes are for emergency access, not the "be ready / carry your own"
     framing the campaign uses, so including them was misleading. This constant
     backs both "Find free Narcan" links (the fentanyl section + the
     "Keep Narcan nearby" card). */
  static NARCAN_FILTER = [{ name: "Narcan (Naloxone)" }];
  static TEST_STRIP_FILTER = [{ name: "Testing Strips" }];
  static CONTINGENCY_FILTER = {
    filters: [{ id: 236, name: "Contingency Management Therapy" }],
  };

  renderTakeActionAccordion(classes) {
    const { openAccordion } = this.state;
    return (
      <div className={classes.accordionList}>
        {takeActionAccordion.map((item, idx) => {
          const isOpen = openAccordion === idx;
          return (
            <div className={classes.accordionItem} key={idx}>
              <button
                type="button"
                className={classes.accordionQuestion}
                aria-expanded={isOpen}
                onClick={() => this.toggleAccordion(idx)}
              >
                <span className={classes.accordionQuestionText}>{item.title}</span>
                {isOpen ? (
                  <KeyboardArrowUpIcon className={classes.accordionIcon} />
                ) : (
                  <KeyboardArrowDownIcon className={classes.accordionIcon} />
                )}
              </button>
              {isOpen && (
                <div className={classes.accordionAnswer}>
                  <ul className={classes.accordionPoints}>
                    {item.points.map((point, pIdx) => (
                      <li key={pIdx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  renderFeatureRow(items, classes) {
    return (
      <div className={classes.featureRow}>
        {items.map((item, idx) => (
          <div
            className={classes.featureItem}
            key={idx}
            style={{ backgroundImage: `url(${item.image})` }}
          >
            <div className={classes.featureOverlay} />
            <div className={classes.featureContent}>
              <h5 className={classes.featureHeading}>{item.title}</h5>
              <p className={classes.featureText}>{item.text}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.pageWrapper}>
        {/* Navbar */}
        <MainNavBar brandText="Harm Reduction" hideAdminLogin {...this.props} />

        {/* ===== Hero / Header 88 — full-width image background, text overlaid ===== */}
        <section className={classes.heroSection}>
          <div
            className={classes.heroCard}
            style={{ backgroundImage: `url(${heroVideoPlaceholder})` }}
          >
            {/* Autoplaying, looping, muted background video. `poster` shows the
                still image until the video loads; muted + playsInline are
                required for mobile autoplay. */}
            <video
              className={classes.heroVideo}
              src={heroVideo}
              poster={heroVideoPlaceholder}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              aria-hidden="true"
            />
            <div className={classes.heroOverlay} />
            <div className={classes.heroColumn}>
              <div className={classes.heroContent}>
                <h1 className={classes.heroTitle}>
                  Stay in control. Know the signs of overdose.
                </h1>
                <p className={classes.heroSubtitle}>
                  The thing about cocaine? It&rsquo;s unpredictable&mdash;and now it&rsquo;s
                  the leading cause of overdose deaths in Cuyahoga County. Protecting
                  yourself and the people around you starts here. Learn how to recognize
                  the signs of an overdose and know what to do next.
                </p>
              </div>
              <div className={classes.heroActions}>
                <Button className={classes.heroButton} onClick={this.scrollToSigns}>
                  Learn the Signs
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ===== Layout 60 — intro two-column (dark) ===== */}
        <section className={`${classes.introSection} ${classes.introSectionDark}`}>
          <div className={classes.introContainer}>
            <div className={classes.introColLeft}>
              <h2>This kind of overdose is different.</h2>
            </div>
            <div className={classes.introColRight}>
              <p>
                It doesn&rsquo;t take a bad batch or a crazy amount to cause a cocaine
                overdose. Those are real risks, of course. But a lot of things can push
                your body past its limits. Being sick or stressed, mixing drugs, not
                getting enough food or water or sleep: it can all make your usual hit,
                hit different.
              </p>
            </div>
          </div>
        </section>

        {/* ===== Layout 626 — Signs of overamping ===== */}
        <section
          id="signs-you-need-to-slow-down"
          ref={this.signsSectionRef}
          className={classes.featuresSection}
        >
          <div className={classes.featuresContainer}>
            {/* Section Title */}
            <div className={classes.sectionTitle}>
              <h2 className={classes.sectionHeading}>Signs you need to slow down</h2>
              <p className={classes.sectionText}>
                Overdosing on cocaine can be scary and dangerous, but it isn&rsquo;t
                always life-threatening. Slowing down at the first signs of distress can
                make a big difference.
              </p>
            </div>

            {/* Row 1: 3 feature items with dividers */}
            {this.renderFeatureRow(featuresRow1, classes)}

            {/* Row 2: 3 feature items with dividers */}
            {this.renderFeatureRow(featuresRow2, classes)}

            {/* Bottom content: heading + copy on the left, accordion on the
                right. Collapses to a single stacked column on mobile. */}
            <div className={classes.bottomContent}>
              <div className={classes.bottomContentColLeft}>
                <h3 className={classes.bottomHeading}>
                  Help yourself or someone else slow down
                </h3>
                <p className={classes.bottomText}>
                  Know what to do when you (or one of your friends) just don&rsquo;t feel
                  right or call Never Use Alone at{" "}
                  <a
                    href="tel:1-800-484-3731"
                    className={classes.bottomTextLink}
                    onClick={() => trackEvent("called_never_use_alone")}
                  >
                    1-800-484-3731
                  </a>{" "}
                  before you use. A real person will stay on the line and
                  send help if something goes wrong. It&rsquo;s safe and confidential.
                </p>
              </div>
              <div className={classes.bottomContentColRight}>
                {this.renderTakeActionAccordion(classes)}
              </div>
            </div>
          </div>
        </section>

        {/* ===== Stats 28 — Signs that immediate medical help is needed ===== */}
        <section className={classes.statsSection}>
          <div className={classes.statsContainer}>
            <div className={classes.sectionTitle}>
              <h2 className={classes.sectionHeading} style={{ fontSize: "32px", lineHeight: "130%" }}>
                Signs you need serious help, immediately
              </h2>
              <p className={classes.sectionText}>
                Recognizing a medical emergency can save your life or the life of someone
                you care about. Cocaine overdose can be deadly, so be ready to act fast
                and call 911 when you see the signs below.
              </p>
            </div>
            <div className={classes.statsGrid}>
              {seriousSigns.map((sign, idx) => (
                <div
                  className={classes.statCard}
                  key={idx}
                  style={{ backgroundImage: `url(${sign.image})` }}
                >
                  <div className={classes.statOverlay} />
                  <div className={classes.statContent}>
                    <h6 className={classes.statNumber}>{sign.title}</h6>
                    <p className={classes.statLabel}>{sign.text}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Sentinel: sits just below the emergency card grid; when it
                enters the viewport, all three cards have been scrolled into
                view (fires viewed_emergency_signs). */}
            <div ref={this.emergencySentinelRef} aria-hidden="true" />
          </div>
        </section>

        {/* ===== CTA 17 — Learn how to call 911 ===== */}
        <section className={classes.ctaSection}>
          <div className={classes.ctaContainer}>
            <div className={classes.ctaComponent}>
              <div className={classes.ctaColLeft}>
                <h2 style={{ fontSize: "28px" }}>
                  Learn how to call 911 without getting hassled
                </h2>
              </div>
              <div className={classes.ctaColRight}>
                <p>
                  In a life-threatening situation, don&rsquo;t wait. Ohio&rsquo;s{" "}
                  <a
                    href="https://odh.ohio.gov/know-our-programs/oh-against-od/good-samaritan-law/call-no-matter-what"
                    className={classes.ctaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("viewed_good_samaritan_law")}
                  >
                    Good Samaritan Law
                  </a>{" "}
                  may protect you from certain charges when you call 911 during an
                  overdose. Remember, you don&rsquo;t have to say it&rsquo;s an overdose
                  when you call for help. Instead, wait with your friend until help
                  arrives, if you can, and tell the paramedics which substances
                  they&rsquo;ve taken.
                </p>
                <p>Not an emergency? You can still talk to someone who understands.</p>
                <div className={classes.ctaActions}>
                  <Button
                    className={classes.ctaButton}
                    onClick={() => {
                      trackEvent("found_local_hotline");
                      this.goToService("/auth/categories/2/hotlines");
                    }}
                  >
                    Find a Local Hotline
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== Layout 60 — When fentanyl is in the mix (dark) ===== */}
        <section className={`${classes.introSection} ${classes.introSectionDark}`}>
          <div className={classes.introContainer}>
            <div className={classes.introColLeft}>
              <h2 style={{ fontSize: "32px" }}>
                Don&rsquo;t let fentanyl take you by surprise.
              </h2>
            </div>
            <div className={classes.introColRight}>
              <p>
                Nodding off, shallow breaths, blue lips and fingernails. An overdose
                looks different when coke gets cut with fentanyl. Keeping Narcan&reg; on
                hand is a smart, easy way to protect yourself and others from a deadly
                overdose you didn&rsquo;t see coming.
              </p>
              <div className={classes.introActions}>
                <Button
                  className={classes.introButton}
                  onClick={() => {
                    trackEvent("found_free_narcan");
                    this.goToService(
                      "/auth/search-service/4/harmreduction",
                      HarmReductionLanding.NARCAN_FILTER
                    );
                  }}
                >
                  Find free Narcan&reg;
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ===== Before you use — heading on top, 3 bordered item cards below ===== */}
        <section className={classes.beforeUseSection}>
          <div className={classes.beforeUseContainer}>
            <div className={classes.beforeUseColLeft}>
              <h2 className={classes.beforeUseHeading}>Before you use</h2>
              <p className={classes.beforeUseDescription}>
                Want to know what&rsquo;s showing up in the cocaine sold near you? Every
                week, Cuyahoga County&rsquo;s Drug Checking Program looks for fentanyl
                and other additives in the local drug supply. You can even search by zip
                code.
              </p>
              <div className={classes.beforeUseLeftActions}>
                <Button
                  className={classes.beforeUseLeftButton}
                  onClick={() => {
                    trackEvent("checked_local_supply");
                    window.open(
                      "https://app.powerbigov.us/view?r=eyJrIjoiMTZkYWM3NjQtMmQzNi00MjEyLThhY2YtZTcxZjRmNTIyMGU2IiwidCI6IjYxYjJiMjcwLTJjY2EtNGRkYy04YTBhLTNkNmRmMjQ2MjdlMyJ9",
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }}
                >
                  Check the Local Supply
                </Button>
              </div>
            </div>
            <div className={classes.beforeUseColRight}>
              {beforeUseItems.map((item, idx) => (
                <div className={classes.beforeUseItem} key={idx}>
                  <img
                    src={item.icon}
                    alt=""
                    className={classes.beforeUseIcon}
                  />
                  <div className={classes.beforeUseItemContent}>
                    <h5 className={classes.beforeUseItemHeading}>{item.title}</h5>
                    <p className={classes.beforeUseItemText}>
                      {item.text}
                      {item.phone && (
                        <a
                          href={`tel:${item.phone}`}
                          className={classes.beforeUseItemLink}
                          style={{ whiteSpace: "nowrap" }}
                          onClick={() => trackEvent("called_never_use_alone")}
                        >
                          {item.phone}
                        </a>
                      )}
                      {item.link && (
                        <a
                          href="#"
                          className={classes.beforeUseItemLink}
                          onClick={(e) => {
                            e.preventDefault();
                            if (item.linkAction === "testStrips") {
                              trackEvent("found_test_strips");
                              this.goToService(
                                "/auth/search-service/4/harmreduction",
                                HarmReductionLanding.TEST_STRIP_FILTER
                              );
                            } else if (item.linkAction === "narcan") {
                              trackEvent("found_free_narcan");
                              this.goToService(
                                "/auth/search-service/4/harmreduction",
                                HarmReductionLanding.NARCAN_FILTER
                              );
                            }
                          }}
                        >
                          {item.link}
                        </a>
                      )}
                      {item.textAfter}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Testimonial 33 (header) — white, outside the black container ===== */}
        <section className={classes.testimonialHeaderSection}>
          <div className={classes.testimonialHeaderInner}>
            <h2 className={classes.testimonialHeading}>
              Hear from people who get it.
            </h2>
            <p className={classes.testimonialSubheading}>
              Get practical safety tips from Ohioans who&rsquo;ve lived with substance
              use and learned from it.
            </p>
            <div className={classes.testimonialLeftActions}>
              <Button
                className={classes.beforeUseLeftButton}
                onClick={() => {
                  trackEvent("found_peer_support");
                  this.goToService(
                    "/auth/search-service/5/peerandfamilysupport"
                  );
                }}
              >
                Find Peer &amp; Family Support
              </Button>
            </div>
          </div>
        </section>

        {/* ===== Testimonial 33 (cards) — black container, QuoteBackground image bg ===== */}
        <section
          className={classes.testimonialSection}
          style={{ backgroundImage: `url(${quoteBackground})` }}
        >
          <div className={classes.testimonialOverlay} />
          <div className={classes.testimonialContainer}>
            <div className={classes.testimonialColRight}>
              {stories.map((story, idx) => (
                <div className={classes.testimonialCard} key={idx}>
                  <p className={classes.testimonialQuote}>
                    &ldquo;{story.quote}&rdquo;
                  </p>
                  <div className={classes.testimonialAttribution}>
                    <p className={classes.testimonialName}>&mdash; {story.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Bottom band: Cocaine deaths + Thinking about quitting, side by side ===== */}
        <section className={classes.bottomBandSection}>
          <div className={classes.bottomBandRow}>
            {/* Left: Cocaine-related deaths statistics */}
            <div className={classes.statsLayoutColLeft}>
              <h2>
                Cocaine&rsquo;s the #1 cause of overdose deaths in Cuyahoga County.
              </h2>
              <p className={classes.bottomBandText}>
                Using alone or with friends, for fun or to get through the day. No matter
                how you do coke, the risks are clear. Knowing a reliable plug or how much
                you can handle just isn&rsquo;t enough. It&rsquo;s important to learn the
                signs of overdose and know what to do next.
              </p>
              <div className={classes.statsNumberGrid}>
                <div className={classes.statsNumberItem}>
                  <h2 className={classes.statsNumberValue}>61%</h2>
                  <p className={classes.statsNumberLabel}>
                    of all recent overdose deaths involved cocaine.
                  </p>
                </div>
                <div className={classes.statsNumberItem}>
                  <h2 className={classes.statsNumberValue}>23%</h2>
                  <p className={classes.statsNumberLabel}>
                    of all recent overdose deaths have involved cocaine plus fentanyl.
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Thinking about quitting CTA */}
            <div className={classes.bottomCtaColumn}>
              <div className={classes.bottomCtaContent}>
                <h2 className={classes.bottomCtaHeading}>
                  Thinking about quitting?
                </h2>
                <p className={classes.bottomCtaText}>
                  There are local programs that can help. Explore your recovery incentive
                  treatment and support options.
                </p>
              </div>
              <div className={classes.ctaActions}>
                <Button
                  className={classes.ctaButton}
                  onClick={() => {
                    trackEvent("found_recovery_services");
                    this.goToService(
                      "/auth/search-service/3/treatmentservices",
                      HarmReductionLanding.CONTINGENCY_FILTER
                    );
                  }}
                >
                  Find Recovery &amp; Support Services
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <FooterNavBar />
      </div>
    );
  }
}

export default withStyles(harmReductionLandingStyle)(HarmReductionLanding);
